// Keyless, browser-CORS Open-Meteo fetchers (verified `access-control-allow-origin: *`)
// + pure category selectors. RN-portable: fetch + math only, no DOM.

import { cToF, mToMi } from '@/lib/nws/units'

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

// ── Air quality (US AQI) ─────────────────────────────────────────────────────
export interface AirQuality {
  usAqi: number | null
}

export async function fetchAirQuality(lat: number, lon: number): Promise<AirQuality> {
  const url = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}&current=us_aqi&timezone=auto`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`AQI ${res.status}`)
  const j = (await res.json()) as { current?: { us_aqi?: number } }
  const v = num(j.current?.us_aqi)
  return { usAqi: v == null ? null : Math.round(v) }
}

export interface AqiMeta {
  label: string
  color: string
}
/** US AQI integer → category word + colour. */
export function aqiCategory(aqi: number | null): AqiMeta {
  if (aqi == null) return { label: '—', color: 'var(--text-3)' }
  // Short labels so the chip subline fits on one line (≤ the ~70px sub width); the dot colour
  // carries the full severity. ('Sensitive' = unhealthy for sensitive groups; 'Severe' = very
  // unhealthy.)
  if (aqi <= 50) return { label: 'Good', color: '#4cae6a' }
  if (aqi <= 100) return { label: 'Moderate', color: '#e6c930' }
  if (aqi <= 150) return { label: 'Sensitive', color: '#ef9f27' }
  if (aqi <= 200) return { label: 'Unhealthy', color: '#e24b4a' }
  if (aqi <= 300) return { label: 'Severe', color: '#8e6fd6' }
  return { label: 'Hazardous', color: '#9a3b3b' }
}

// ── Forecast current (UV, dew point, visibility) — used directly for UV and as the
//    fallback for dew point / visibility when the NWS station value is null. ────────
export interface OpenMeteoCurrent {
  uvIndex: number | null
  dewPointF: number | null
  visibilityMi: number | null
}

export async function fetchOpenMeteoCurrent(lat: number, lon: number): Promise<OpenMeteoCurrent> {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=uv_index,dew_point_2m,visibility&timezone=auto`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`Open-Meteo ${res.status}`)
  const c = ((await res.json()) as { current?: Record<string, number> }).current ?? {}
  const dp = num(c.dew_point_2m)
  const vis = num(c.visibility)
  return {
    uvIndex: num(c.uv_index),
    dewPointF: dp == null ? null : cToF(dp),
    visibilityMi: vis == null ? null : mToMi(vis),
  }
}

/** UV index → descriptor band. */
export function uvDescriptor(uv: number | null): string {
  if (uv == null) return '—'
  if (uv <= 2) return 'low'
  if (uv <= 5) return 'moderate'
  if (uv <= 7) return 'high'
  if (uv <= 10) return 'very high'
  return 'extreme'
}

/** Dew-point comfort descriptor (°F). Short words so "dp 73° · <feel>" fits one chip line. */
export function dewPointFeel(dpF: number | null): string {
  if (dpF == null) return ''
  if (dpF >= 70) return 'muggy'
  if (dpF >= 60) return 'humid'
  if (dpF >= 55) return 'comfy'
  if (dpF >= 45) return 'nice'
  return 'dry'
}

/** Visibility descriptor (miles). */
export function visibilityFeel(mi: number | null): string {
  if (mi == null) return ''
  if (mi >= 9) return 'clear'
  if (mi >= 6) return 'haze'
  if (mi >= 3) return 'hazy'
  if (mi >= 1) return 'misty'
  return 'fog'
}
